// src/QRScanner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// ============================================
// API URL - Production URL with /api
// ============================================
const API_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

// ✅ VoiceInput Component - Speech to Text
const VoiceInput = ({ onTranscript, fieldId, placeholder }) => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const isFinalizedRef = useRef(false);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Voice input not supported in this browser');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('🎤 Voice recognition started');
      setIsListening(true);
      setError('');
      finalTranscriptRef.current = '';
      isFinalizedRef.current = false;
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcriptText = result[0].transcript;
        
        if (result.isFinal) {
          finalTranscript += transcriptText + ' ';
          finalTranscriptRef.current += transcriptText + ' ';
          isFinalizedRef.current = true;
        } else {
          interimTranscript += transcriptText;
        }
      }

      const currentText = (finalTranscriptRef.current + interimTranscript).trim();
      setTranscript(currentText);
      
      if (onTranscript) {
        onTranscript(currentText);
      }
    };

    recognition.onerror = (event) => {
      console.error('🎤 Voice recognition error:', event.error);
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        setError('Microphone access denied. Please allow microphone access.');
      } else if (event.error === 'no-speech') {
        setError('No speech detected. Please try again.');
      } else {
        setError(`Voice error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      console.log('🎤 Voice recognition ended');
      setIsListening(false);
      
      if (finalTranscriptRef.current.trim()) {
        setTranscript(finalTranscriptRef.current.trim());
        if (onTranscript) {
          onTranscript(finalTranscriptRef.current.trim());
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, [onTranscript]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      setError('Voice input not supported');
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.error('Error stopping:', e);
      }
      setIsListening(false);
    } else {
      try {
        finalTranscriptRef.current = '';
        isFinalizedRef.current = false;
        setTranscript('');
        setError('');
        recognitionRef.current.start();
      } catch (e) {
        console.error('Error starting:', e);
        try {
          recognitionRef.current.stop();
          setTimeout(() => {
            try {
              recognitionRef.current.start();
            } catch (err) {
              setError('Could not start voice input');
            }
          }, 300);
        } catch (err) {
          setError('Could not start voice input');
        }
      }
    }
  };

  return (
    <div style={styles.voiceContainer}>
      <button
        type="button"
        onClick={toggleListening}
        style={{
          ...styles.voiceBtn,
          ...(isListening ? styles.voiceBtnActive : {}),
          ...(error ? styles.voiceBtnError : {})
        }}
        title={isListening ? 'Stop recording' : 'Start voice input'}
      >
        <span style={styles.voiceIcon}>
          {isListening ? '⏹️' : '🎤'}
        </span>
        <span style={styles.voiceText}>
          {isListening ? 'Listening...' : error || 'Speak'}
        </span>
        {isListening && (
          <span style={styles.voicePulse}>
            <span style={styles.pulseDot}></span>
          </span>
        )}
      </button>
      
      {transcript && !isListening && (
        <div style={styles.voiceTranscript}>
          <span style={styles.transcriptIcon}>💬</span>
          <span style={styles.transcriptText}>{transcript}</span>
        </div>
      )}
      
      {error && !isListening && (
        <div style={styles.voiceError}>
          <span>⚠️</span> {error}
        </div>
      )}
    </div>
  );
};

// ✅ RatingMessageModal
const RatingMessageModal = ({ 
  showRatingMessage, 
  tempRating, 
  currentRatingField, 
  formData, 
  handleFieldChange, 
  handleRatingMessageSubmit,
  setShowRatingMessage,
  setCurrentRatingField,
  setTempRating 
}) => {
  if (!showRatingMessage) return null;

  const getRatingMessage = (rating) => {
    const messages = {
      1: "😔 We're sorry to hear that. Please tell us what went wrong.",
      2: "😕 Thank you for your feedback. What could we do better?",
      3: "🙂 We appreciate your feedback. How can we improve?",
      4: "😊 Great! What did you like most?",
      5: "🌟 Excellent! Please share your wonderful experience with us!"
    };
    return messages[rating] || "Please share your feedback with us.";
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Tell us why you gave {tempRating} stars</h3>
        </div>
        
        <div style={styles.modalBody}>
          <p style={styles.modalText}>
            {getRatingMessage(tempRating)}
          </p>
          
          <div style={styles.modalInputGroup}>
            <label style={styles.modalLabel}>
              Your Feedback <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <textarea
                key="modal-textarea-fixed"
                style={{ ...styles.modalTextarea, paddingRight: '50px' }}
                placeholder={`Please tell us in your own words why you gave ${tempRating} star${tempRating > 1 ? 's' : ''}...`}
                value={formData[`${currentRatingField?.id}_description`] || ''}
                onChange={(e) => {
                  handleFieldChange(`${currentRatingField?.id}_description`, e.target.value);
                }}
                rows="4"
                autoFocus
              />
              <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
                <VoiceInput 
                  onTranscript={(text) => {
                    const fieldId = `${currentRatingField?.id}_description`;
                    const currentText = formData[fieldId] || '';
                    handleFieldChange(fieldId, currentText + (currentText ? ' ' : '') + text);
                  }}
                  fieldId={`${currentRatingField?.id}_description`}
                  placeholder="Speak your feedback..."
                />
              </div>
            </div>
            <small style={styles.modalHint}>
              Your honest feedback helps us improve our service
            </small>
          </div>
        </div>
        
        <div style={styles.modalFooter}>
          <button
            onClick={() => {
              setShowRatingMessage(false);
              setCurrentRatingField(null);
              setTempRating(0);
            }}
            style={styles.modalCancelBtn}
          >
            Cancel
          </button>
          <button
            onClick={handleRatingMessageSubmit}
            style={styles.modalSubmitBtn}
          >
            ✅ Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
};

const QRScanner = () => {
  const navigate = useNavigate();
  const { qrId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [error, setError] = useState('');
  const [qrData, setQrData] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [officeName, setOfficeName] = useState('');
  const [formData, setFormData] = useState({});
  const [userInfo, setUserInfo] = useState({ name: '', email: '', phone: '' });
  const [showRatingMessage, setShowRatingMessage] = useState(false);
  const [currentRatingField, setCurrentRatingField] = useState(null);
  const [tempRating, setTempRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // AUDIO RECORDING
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  useEffect(() => {
    console.log('QR Scanner mounted, qrId:', qrId);
    console.log('📡 API URL:', API_URL);
    if (qrId) {
      loadQRData(qrId);
    } else {
      setError('Invalid QR Code');
      setLoading(false);
    }
  }, [qrId]);

  // ============================================
  // HELPER: Parse options from JSON string
  // ============================================
  const parseOptions = (options) => {
    if (!options) return [];
    if (Array.isArray(options)) return options;
    if (typeof options === 'string') {
      try {
        const parsed = JSON.parse(options);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // If not JSON, split by comma
        return options.split(',').map(item => item.trim()).filter(Boolean);
      }
    }
    return [];
  };

  // ============================================
  // LOAD QR DATA
  // ============================================
  const loadQRData = async (id) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${API_URL}/api/qr/public/${id}`
      );

      const data = await response.json();

      console.log('📥 QR data:', data);

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || 'Failed to load QR data'
        );
      }

      const qr = data.data;

      setQrData(qr);
      setOfficeName(
        qr.office_name || 'Feedback Form'
      );

      if (qr.fields && qr.fields.length > 0) {
        const fields = qr.fields.map((f) => {
          const fieldId = f.field_name || f.id;

          const fieldType = String(
            f.field_type ||
            f.type ||
            'text'
          )
            .trim()
            .toLowerCase();

          let mappedType = 'text';

          if (
            fieldType === 'textarea' ||
            fieldType === 'text-area' ||
            fieldType === 'text area'
          ) {
            mappedType = 'textarea';
          } else if (
            fieldType === 'rating' ||
            fieldType === 'star-rating' ||
            fieldType === 'stars'
          ) {
            mappedType = 'rating';
          } else if (
            fieldType === 'select' ||
            fieldType === 'dropdown' ||
            fieldType === 'drop-down'
          ) {
            mappedType = 'select';
          } else if (
            fieldType === 'number' ||
            fieldType === 'numeric'
          ) {
            mappedType = 'number';
          } else if (
            fieldType === 'date' ||
            fieldType === 'date-picker' ||
            fieldType === 'datepicker'
          ) {
            mappedType = 'date';
          } else if (
            fieldType === 'email' ||
            fieldType === 'email-address'
          ) {
            mappedType = 'email';
          } else if (
            fieldType === 'tel' ||
            fieldType === 'phone' ||
            fieldType === 'telephone'
          ) {
            mappedType = 'tel';
          } else if (fieldType === 'radio') {
            mappedType = 'radio';
          } else if (fieldType === 'checkbox') {
            mappedType = 'checkbox';
          }

          // ✅ Parse options properly
          let options = parseOptions(f.options);

          console.log(`📋 Field "${f.field_label}" type: ${mappedType}, options:`, options);

          return {
            id: fieldId,
            label: f.field_label || f.label || fieldId,
            type: mappedType,
            originalType: f.field_type,
            required: f.is_required === 1 || f.required === true,
            ratingThreshold: f.rating_threshold || f.ratingThreshold || 3,
            descriptionRequired: true,
            options: options
          };
        });

        setFormFields(fields);

        const initialFormData = {};

        fields.forEach((field) => {
          // For checkbox, initialize as empty array
          if (field.type === 'checkbox') {
            initialFormData[field.id] = [];
          } else {
            initialFormData[field.id] = '';
          }

          if (field.type === 'rating') {
            initialFormData[`${field.id}_description`] = '';
          }
        });

        setFormData(initialFormData);
      }
    } catch (error) {
      console.error('❌ QR loading error:', error);
      setError(error.message || 'Unable to load QR code');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (e) {}
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // ============================================
  // HANDLE CHECKBOX CHANGE
  // ============================================
  const handleCheckboxChange = (fieldId, option, checked) => {
    setFormData(prev => {
      const currentValues = prev[fieldId] || [];
      let newValues;
      
      if (checked) {
        newValues = [...currentValues, option];
      } else {
        newValues = currentValues.filter(val => val !== option);
      }
      
      return { ...prev, [fieldId]: newValues };
    });
  };

  // ============================================
  // AUDIO RECORDING
  // ============================================
  const startAudioRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        alert('Audio recording is not supported in this browser.');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];

      const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mimeType = types.find(type => MediaRecorder.isTypeSupported(type));

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, {
          type: recorder.mimeType || 'audio/webm'
        });
        const url = URL.createObjectURL(blob);

        setAudioBlob(blob);
        setAudioUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });

        stream.getTracks().forEach(track => track.stop());
      };

      recorder.onerror = (event) => {
        console.error('Audio recorder error:', event.error);
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Microphone error:', error);
      alert('Could not access microphone. Please allow microphone permission.');
      setIsRecording(false);
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorderRef.current?.state !== 'inactive') {
      mediaRecorderRef.current?.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setIsRecording(false);
  };

  const removeAudioRecording = () => {
    if (isRecording) stopAudioRecording();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl('');
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  // ============================================
  // PERFORM SUBMIT
  // ============================================
  const performSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      if (!qrId) {
        throw new Error('Invalid QR Code');
      }

      const qrHistory = JSON.parse(localStorage.getItem('qrHistory') || '[]');
      const qrDataFound = qrHistory.find(q => q.id === qrId || q.qr_id === qrId);
      const officeNameFromStorage = qrDataFound?.office_name || qrDataFound?.qr_name || officeName || 'Feedback Form';

      const formDataToSave = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          // For checkbox arrays, store as JSON string
          if (Array.isArray(value)) {
            formDataToSave[key] = JSON.stringify(value);
          } else {
            formDataToSave[key] = value;
          }
        }
      });

      const submission = {
        id: `sub_${Date.now()}`,
        submission_id: `sub_${Date.now()}`,
        qrId: qrId,
        office_name: officeNameFromStorage,
        user_name: userInfo.name?.trim() || 'Anonymous',
        user_email: userInfo.email?.trim() || '-',
        user_phone: userInfo.phone?.trim() || '-',
        form_data: formDataToSave,
        created_at: new Date().toISOString(),
        audio_feedback: null
      };

      const storageKey = `submissions_${qrId}`;
      const existingSubmissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existingSubmissions.unshift(submission);
      localStorage.setItem(storageKey, JSON.stringify(existingSubmissions));

      try {
        console.log('📤 Submitting to database:', {
          url: `${API_URL}/api/qr/submit/${qrId}`,
          formData: formDataToSave,
          userInfo: {
            name: userInfo.name?.trim() || 'Anonymous',
            email: userInfo.email?.trim() || '-',
            phone: userInfo.phone?.trim() || '-'
          }
        });

        const response = await fetch(`${API_URL}/api/qr/submit/${qrId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            formData: formDataToSave,
            userInfo: {
              name: userInfo.name?.trim() || 'Anonymous',
              email: userInfo.email?.trim() || '-',
              phone: userInfo.phone?.trim() || '-'
            }
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Data saved to database:', data);
          if (data.submissionId) {
            submission.database_id = data.submissionId;
            const updatedSubmissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
            const index = updatedSubmissions.findIndex(s => s.id === submission.id);
            if (index !== -1) {
              updatedSubmissions[index].database_id = data.submissionId;
              localStorage.setItem(storageKey, JSON.stringify(updatedSubmissions));
            }
          }
        } else {
          const errorData = await response.json();
          console.warn('⚠️ Database save failed:', errorData.message || 'Unknown error');
        }
      } catch (dbError) {
        console.warn('⚠️ Database error but data saved in localStorage:', dbError);
      }

      if (audioBlob) {
        try {
          const textParts = Object.entries(formData)
            .filter(([key, value]) => !key.endsWith('_description') && value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            });

          const descriptionParts = Object.entries(formData)
            .filter(([key, value]) => key.endsWith('_description') && value !== '' && value !== null && value !== undefined)
            .map(([key, value]) => `${key}: ${value}`);

          const notes = [
            `QR_ID: ${qrId}`,
            `OFFICE_NAME: ${officeNameFromStorage}`,
            `NAME: ${userInfo.name?.trim() || ''}`,
            `EMAIL: ${userInfo.email?.trim() || ''}`,
            `PHONE: ${userInfo.phone?.trim() || ''}`,
            '',
            'FORM RESPONSES:',
            ...textParts,
            '',
            'RATING DESCRIPTIONS:',
            ...descriptionParts
          ].join('\n');

          const audioForm = new FormData();
          audioForm.append('name', userInfo.name?.trim() || 'Anonymous');
          
          const extension = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
          audioForm.append('audio', audioBlob, `qr-${qrId}-feedback-${Date.now()}.${extension}`);
          audioForm.append('notes', notes);
          audioForm.append('duration_seconds', String(recordingTime || 0));

          const audioResponse = await fetch(`${API_URL}/api/audio-feedback`, {
            method: 'POST',
            body: audioForm
          });

          if (audioResponse.ok) {
            const audioData = await audioResponse.json();
            if (audioData.success) {
              submission.audio_feedback = {
                id: audioData.data?.id || audioData.data?.public_id,
                audioUrl: audioData.data?.audio_url || audioData.data?.audioUrl,
                notes: notes
              };
              const updatedSubmissions = JSON.parse(localStorage.getItem(storageKey) || '[]');
              const index = updatedSubmissions.findIndex(s => s.id === submission.id);
              if (index !== -1) {
                updatedSubmissions[index].audio_feedback = submission.audio_feedback;
                localStorage.setItem(storageKey, JSON.stringify(updatedSubmissions));
              }
              console.log('✅ Audio uploaded successfully');
            }
          } else {
            console.warn('⚠️ Audio upload failed');
          }
        } catch (audioError) {
          console.warn('Audio upload error:', audioError);
        }
      }

      const updatedHistory = qrHistory.map(q => {
        if (q.id === qrId || q.qr_id === qrId) {
          return { ...q, response_count: (q.response_count || 0) + 1 };
        }
        return q;
      });
      localStorage.setItem('qrHistory', JSON.stringify(updatedHistory));

      setSubmissionId(submission.id);
      setSubmitted(true);
      setFormData({});
      setUserInfo({ name: '', email: '', phone: '' });
      removeAudioRecording();
      setShowRatingMessage(false);
      setCurrentRatingField(null);
      setTempRating(0);

      console.log('✅ Feedback submitted successfully!', submission);

    } catch (error) {
      console.error('❌ Submit error:', error);
      setError(error.message || 'Failed to submit feedback');
      alert(error.message || 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================
  // HANDLE SUBMIT
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    let isValid = true;

    for (const field of formFields) {
      if (field.required) {
        const value = formData[field.id];
        
        if (field.type === 'checkbox') {
          // For checkbox, check if array is empty
          if (!value || (Array.isArray(value) && value.length === 0)) {
            alert(`Please select at least one option for ${field.label}`);
            isValid = false;
            break;
          }
        } else if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          alert(`${field.label} is required`);
          isValid = false;
          break;
        }
      }

      if (field.type === 'rating') {
        const rating = parseInt(formData[field.id]);
        if (rating > 0) {
          const description = formData[`${field.id}_description`];
          if (!description || description.trim() === '') {
            setCurrentRatingField(field);
            setTempRating(rating);
            setShowRatingMessage(true);
            isValid = false;
            break;
          }
        } else {
          alert(`Please rate ${field.label}`);
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) return;

    await performSubmit();
  };

  const handleRatingMessageSubmit = () => {
    const description = formData[`${currentRatingField.id}_description`];
    if (!description || description.trim() === '') {
      alert('Please write your feedback before submitting');
      return;
    }
    
    setShowRatingMessage(false);
    setCurrentRatingField(null);
    setTempRating(0);
    
    performSubmit();
  };

  // ============================================
  // StarRating Component
  // ============================================
  const StarRating = ({ field, value, onChange }) => {
    const [rating, setRating] = useState(value || 0);
    const [description, setDescription] = useState('');
    const [showDescription, setShowDescription] = useState(false);

    const handleClick = (star) => {
      setRating(star);
      onChange(field.id, star);
      setShowDescription(true);
      setDescription('');
      onChange(`${field.id}_description`, '');
    };

    const handleDescriptionChange = (e) => {
      const value = e.target.value;
      setDescription(value);
      onChange(`${field.id}_description`, value);
    };

    const handleVoiceInput = (transcript) => {
      const currentText = description || '';
      const newText = currentText + (currentText ? ' ' : '') + transcript;
      setDescription(newText);
      onChange(`${field.id}_description`, newText);
    };

    const getRatingMessage = (rating) => {
      const messages = {
        1: "😔 We're sorry to hear that. What went wrong?",
        2: "😕 Thank you. What could we do better?",
        3: "🙂 We appreciate your feedback. How can we improve?",
        4: "😊 Great! What did you like most?",
        5: "🌟 Excellent! Please share your wonderful experience!"
      };
      return messages[rating] || "Please share your feedback with us.";
    };

    return (
      <div style={styles.ratingContainer}>
        <div style={styles.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              onClick={() => handleClick(star)}
              style={{
                fontSize: '48px',
                cursor: 'pointer',
                color: star <= rating ? '#FFD700' : '#ddd',
                transition: 'all 0.3s',
                textShadow: star <= rating ? '0 0 20px rgba(255,215,0,0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              ★
            </span>
          ))}
        </div>
        
        {showDescription && rating > 0 && (
          <div style={styles.descContainer}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={styles.descLabel}>
                📝 {getRatingMessage(rating)} <span style={{ color: 'red' }}>*</span>
              </label>
              <VoiceInput 
                onTranscript={handleVoiceInput}
                fieldId={`${field.id}_description`}
                placeholder="Speak your feedback..."
              />
            </div>
            <textarea
              key={`desc-${field.id}-${rating}`}
              style={{ ...styles.descTextarea, paddingRight: '50px' }}
              placeholder={`Please tell us in your own words why you gave ${rating} star${rating > 1 ? 's' : ''}...`}
              value={description}
              onChange={handleDescriptionChange}
              rows="4"
              required
            />
            <small style={styles.descRequired}>
              ⚠️ Your feedback is required for all ratings
            </small>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RADIO BUTTON RENDER
  // ============================================
  const renderRadioField = (field) => {
    const value = formData[field.id] || '';
    const options = field.options || [];

    console.log(`🔘 Rendering radio field "${field.label}" with options:`, options);

    if (options.length === 0) {
      return (
        <div style={{ color: '#999', fontSize: '14px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
          ⚠️ No options available for this radio button
        </div>
      );
    }

    return (
      <div style={styles.radioGroup}>
        {options.map((option, index) => (
          <label key={index} style={styles.radioLabel}>
            <input
              type="radio"
              name={field.id}
              value={option}
              checked={value === option}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              style={styles.radioInput}
              required={field.required}
            />
            <span style={styles.radioText}>{option}</span>
          </label>
        ))}
      </div>
    );
  };

  // ============================================
  // CHECKBOX RENDER
  // ============================================
  const renderCheckboxField = (field) => {
    const selectedValues = formData[field.id] || [];
    const options = field.options || [];

    console.log(`☑️ Rendering checkbox field "${field.label}" with options:`, options);

    if (options.length === 0) {
      return (
        <div style={{ color: '#999', fontSize: '14px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
          ⚠️ No options available for this checkbox
        </div>
      );
    }

    return (
      <div style={styles.checkboxGroup}>
        {options.map((option, index) => (
          <label key={index} style={styles.checkboxLabel}>
            <input
              type="checkbox"
              value={option}
              checked={selectedValues.includes(option)}
              onChange={(e) => handleCheckboxChange(field.id, option, e.target.checked)}
              style={styles.checkboxInput}
              required={field.required && selectedValues.length === 0}
            />
            <span style={styles.checkboxText}>{option}</span>
          </label>
        ))}
        {field.required && selectedValues.length === 0 && (
          <span style={styles.checkboxRequired}>Please select at least one option</span>
        )}
      </div>
    );
  };

  // ============================================
  // SELECT/DROPDOWN RENDER
  // ============================================
  const renderSelectField = (field) => {
    const value = formData[field.id] || '';
    const options = field.options || [];

    console.log(`📋 Rendering dropdown field "${field.label}" with options:`, options);

    if (options.length === 0) {
      return (
        <div style={{ color: '#999', fontSize: '14px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
          ⚠️ No options available for this dropdown
        </div>
      );
    }

    return (
      <select
        key={field.id}
        style={styles.select}
        value={value}
        onChange={(e) => handleFieldChange(field.id, e.target.value)}
        required={field.required}
      >
        <option value="">Select {field.label}...</option>
        {options.map((opt, idx) => (
          <option key={idx} value={opt}>{opt}</option>
        ))}
      </select>
    );
  };

  const renderFieldInput = (field) => {
    const value = formData[field.id] !== undefined ? formData[field.id] : '';

    switch(field.type) {
      case 'radio':
        return renderRadioField(field);
      
      case 'checkbox':
        return renderCheckboxField(field);
      
      case 'select':
        return renderSelectField(field);
      
      case 'rating':
        return (
          <StarRating
            key={field.id}
            field={field}
            value={formData[field.id]}
            onChange={handleFieldChange}
          />
        );
      
      case 'textarea':
        return (
          <div style={{ position: 'relative' }}>
            <textarea
              key={field.id}
              style={{ ...styles.textarea, paddingRight: '50px' }}
              placeholder={`Enter your ${field.label.toLowerCase()}...`}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              rows="4"
              required={field.required}
            />
            <div style={{ position: 'absolute', bottom: '10px', right: '10px' }}>
              <VoiceInput 
                onTranscript={(text) => {
                  const currentText = formData[field.id] || '';
                  handleFieldChange(field.id, currentText + (currentText ? ' ' : '') + text);
                }}
                fieldId={field.id}
                placeholder={`Speak your ${field.label}...`}
              />
            </div>
          </div>
        );
      
      case 'date':
        return (
          <input
            key={field.id}
            type="date"
            style={styles.input}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
          />
        );
      
      case 'number':
        return (
          <input
            key={field.id}
            type="number"
            style={styles.input}
            placeholder={`Enter your ${field.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
          />
        );
      
      case 'email':
        return (
          <input
            key={field.id}
            type="email"
            style={styles.input}
            placeholder={`Enter your ${field.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
          />
        );
      
      case 'tel':
        return (
          <input
            key={field.id}
            type="tel"
            style={styles.input}
            placeholder={`Enter your ${field.label.toLowerCase()}...`}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
          />
        );
      
      default:
        return (
          <div style={{ position: 'relative' }}>
            <input
              key={field.id}
              type="text"
              style={{ ...styles.input, paddingRight: '50px' }}
              placeholder={`Enter your ${field.label.toLowerCase()}...`}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              required={field.required}
            />
            <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
              <VoiceInput 
                onTranscript={(text) => {
                  const currentText = formData[field.id] || '';
                  handleFieldChange(field.id, currentText + (currentText ? ' ' : '') + text);
                }}
                fieldId={field.id}
                placeholder={`Speak your ${field.label}...`}
              />
            </div>
          </div>
        );
    }
  };

  // --- Loading, Error, Submitted States ---
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>Loading feedback form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ color: '#dc3545' }}>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} style={styles.submitBtn}>Go Home</button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={{...styles.card, textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
          <h2 style={{ color: '#28a745' }}>Thank You!</h2>
          <p>Your feedback has been submitted successfully.</p>
          <p style={{ color: '#999', fontSize: '14px' }}>Submission ID: {submissionId}</p>
          <button 
            onClick={() => {
              setSubmitted(false);
              setFormData({});
              setUserInfo({ name: '', email: '', phone: '' });
            }} 
            style={styles.submitBtn}
          >
            Submit Another Response
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>{officeName}</h1>
          <p style={styles.subtitle}>We value your feedback!</p>
          <p style={styles.subtitleSmall}>Please take a moment to share your experience</p>
        </div>

        <form onSubmit={handleSubmit}>
          {formFields.length > 0 && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Feedback Questions</h3>
              {formFields.map((field, index) => (
                <div key={field.id} style={styles.fieldCard}>
                  <div style={styles.fieldHeader}>
                    <span style={styles.fieldNumber}>{index + 1}</span>
                    <label style={styles.fieldLabel}>
                      {field.label}
                      {field.required && <span style={styles.requiredStar}> *</span>}
                    </label>
                    <span style={{ fontSize: '12px', color: '#999', marginLeft: 'auto' }}>
                      ({field.type})
                    </span>
                  </div>

                  {renderFieldInput(field)}
                </div>
              ))}
            </div>
          )}

          <div style={styles.audioSection}>
            <div style={styles.audioHeader}>
              <div>
                <h3 style={styles.audioTitle}>
                  🎙️ Audio Feedback <span style={styles.optionalText}>(Optional)</span>
                </h3>
                <p style={styles.audioSubtitle}>
                  Record your feedback in your own voice. The audio will be saved with this response.
                </p>
              </div>
            </div>

            {!isRecording && !audioUrl && (
              <button
                type="button"
                onClick={startAudioRecording}
                style={styles.recordBtn}
                disabled={isSubmitting}
              >
                <span style={styles.recordDot}></span>
                Start Recording
              </button>
            )}

            {isRecording && (
              <button
                type="button"
                onClick={stopAudioRecording}
                style={styles.stopRecordBtn}
              >
                <span style={styles.stopSquare}></span>
                Stop Recording
                <span style={styles.recordTimer}>{formatRecordingTime(recordingTime)}</span>
              </button>
            )}

            {!isRecording && audioUrl && (
              <div style={styles.audioPreview}>
                <div style={styles.audioPreviewTop}>
                  <span style={styles.audioReady}>✓ Audio recorded</span>
                  <span style={styles.audioDuration}>{formatRecordingTime(recordingTime)}</span>
                </div>
                <audio controls src={audioUrl} style={styles.audioPlayer} />
                <button
                  type="button"
                  onClick={removeAudioRecording}
                  style={styles.removeAudioBtn}
                >
                  🗑 Remove & Record Again
                </button>
              </div>
            )}
          </div>

          <button 
            type="submit" 
            style={styles.submitBtn}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>Thank you for your valuable feedback!</p>
        </div>
      </div>

      <RatingMessageModal
        showRatingMessage={showRatingMessage}
        tempRating={tempRating}
        currentRatingField={currentRatingField}
        formData={formData}
        handleFieldChange={handleFieldChange}
        handleRatingMessageSubmit={handleRatingMessageSubmit}
        setShowRatingMessage={setShowRatingMessage}
        setCurrentRatingField={setCurrentRatingField}
        setTempRating={setTempRating}
      />
    </div>
  );
};

// ============================================
// STYLES
// ============================================
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #e0e0e0',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: '20px',
    color: '#666',
    fontSize: '18px'
  },
  card: {
    background: 'white',
    padding: '40px',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
    maxWidth: '700px',
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: '35px',
    paddingBottom: '25px',
    borderBottom: '3px solid #667eea'
  },
  title: {
    margin: '0',
    color: '#1a1a2e',
    fontSize: '28px',
    fontWeight: '700'
  },
  subtitle: {
    color: '#667eea',
    fontSize: '16px',
    marginTop: '5px',
    fontWeight: '500'
  },
  subtitleSmall: {
    color: '#999',
    fontSize: '14px',
    marginTop: '5px'
  },
  section: {
    marginBottom: '30px'
  },
  sectionTitle: {
    color: '#333',
    fontSize: '18px',
    marginBottom: '20px',
    paddingBottom: '10px',
    borderBottom: '2px solid #f0f0f0'
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s',
    outline: 'none'
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s',
    resize: 'vertical',
    outline: 'none'
  },
  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    background: 'white',
    transition: 'border-color 0.3s',
    outline: 'none'
  },
  fieldCard: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '15px',
    border: '1px solid #e8ecf1',
    transition: 'box-shadow 0.3s'
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px'
  },
  fieldNumber: {
    background: '#667eea',
    color: 'white',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0
  },
  fieldLabel: {
    fontWeight: '600',
    color: '#333',
    fontSize: '16px'
  },
  requiredStar: {
    color: '#dc3545',
    marginLeft: '4px'
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 0',
    width: '100%'
  },
  stars: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  ratingLabel: {
    marginTop: '10px',
    fontSize: '16px',
    fontWeight: '500',
    color: '#333'
  },
  descContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
    marginTop: '15px',
    padding: '15px',
    background: '#f0f7ff',
    borderRadius: '8px',
    border: '2px solid #667eea',
    animation: 'fadeIn 0.3s ease'
  },
  descLabel: {
    fontSize: '15px',
    color: '#333',
    fontWeight: '600'
  },
  descTextarea: {
    padding: '10px 12px',
    border: '2px solid #667eea',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '100px',
    resize: 'vertical',
    transition: 'border-color 0.3s',
    backgroundColor: 'white',
    outline: 'none'
  },
  descRequired: {
    fontSize: '12px',
    color: '#667eea',
    fontWeight: '500'
  },
  submitBtn: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    marginTop: '10px'
  },
  homeBtn: {
    padding: '12px 30px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    marginTop: '10px'
  },
  audioSection: {
    marginTop: '25px',
    marginBottom: '20px',
    padding: '20px',
    background: '#f8fbff',
    border: '1px solid #dce8f5',
    borderRadius: '12px'
  },
  audioHeader: {
    marginBottom: '16px'
  },
  audioTitle: {
    margin: 0,
    color: '#1a1a2e',
    fontSize: '17px',
    fontWeight: '700'
  },
  optionalText: {
    color: '#8a94a6',
    fontSize: '12px',
    fontWeight: '500'
  },
  audioSubtitle: {
    margin: '6px 0 0',
    color: '#718096',
    fontSize: '13px',
    lineHeight: '1.5'
  },
  recordBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '11px 18px',
    background: '#fff',
    color: '#d64545',
    border: '1px solid #efb1b1',
    borderRadius: '9px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  recordDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: '#e53935',
    display: 'inline-block'
  },
  stopRecordBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    padding: '11px 18px',
    background: '#fff4f4',
    color: '#c62828',
    border: '1px solid #ef9a9a',
    borderRadius: '9px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  stopSquare: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
    background: '#c62828',
    display: 'inline-block'
  },
  recordTimer: {
    fontFamily: 'monospace'
  },
  audioPreview: {
    background: 'white',
    border: '1px solid #dce8f5',
    borderRadius: '10px',
    padding: '14px'
  },
  audioPreviewTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  audioReady: {
    color: '#218838',
    fontSize: '13px',
    fontWeight: '700'
  },
  audioDuration: {
    color: '#718096',
    fontSize: '12px',
    fontFamily: 'monospace'
  },
  audioPlayer: {
    width: '100%',
    height: '40px'
  },
  removeAudioBtn: {
    marginTop: '10px',
    padding: '7px 10px',
    background: 'transparent',
    color: '#c62828',
    border: 'none',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  footer: {
    marginTop: '30px',
    textAlign: 'center',
    paddingTop: '20px',
    borderTop: '1px solid #f0f0f0'
  },
  footerText: {
    color: '#999',
    fontSize: '14px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.3s ease'
  },
  modalContent: {
    background: 'white',
    padding: '35px',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '95%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.3s ease'
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0'
  },
  modalIcon: {
    fontSize: '32px'
  },
  modalTitle: {
    margin: 0,
    color: '#1a1a2e',
    fontSize: '20px',
    fontWeight: '700'
  },
  modalBody: {
    marginBottom: '25px'
  },
  modalText: {
    color: '#555',
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '20px'
  },
  modalInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  modalLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  modalTextarea: {
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'border-color 0.3s',
    outline: 'none'
  },
  modalHint: {
    fontSize: '12px',
    color: '#999',
    marginTop: '5px'
  },
  modalFooter: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '15px',
    borderTop: '1px solid #f0f0f0'
  },
  modalCancelBtn: {
    padding: '10px 25px',
    background: '#f0f0f0',
    color: '#666',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.3s'
  },
  modalSubmitBtn: {
    padding: '10px 25px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s'
  },
  voiceContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  voiceBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    borderRadius: '20px',
    border: '2px solid #e0e0e0',
    background: '#f8f9fa',
    color: '#666',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: '500',
    minWidth: '80px',
    justifyContent: 'center'
  },
  voiceBtnActive: {
    background: '#667eea',
    borderColor: '#667eea',
    color: 'white',
    animation: 'pulse 1.5s ease-in-out infinite'
  },
  voiceBtnError: {
    borderColor: '#dc3545',
    color: '#dc3545'
  },
  voiceIcon: {
    fontSize: '18px'
  },
  voiceText: {
    fontSize: '12px'
  },
  voicePulse: {
    display: 'flex',
    alignItems: 'center'
  },
  pulseDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#ff6b6b',
    display: 'inline-block',
    animation: 'pulseDot 1s ease-in-out infinite'
  },
  voiceTranscript: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: '#e8f5e9',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#2e7d32'
  },
  transcriptIcon: {
    fontSize: '14px'
  },
  transcriptText: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  voiceError: {
    fontSize: '11px',
    color: '#dc3545',
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  // Radio button styles
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 0'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '6px',
    transition: 'background 0.2s',
    hover: {
      background: '#f0f0f0'
    }
  },
  radioInput: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#667eea'
  },
  radioText: {
    color: '#333'
  },
  // Checkbox styles
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 0'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '6px',
    transition: 'background 0.2s',
    hover: {
      background: '#f0f0f0'
    }
  },
  checkboxInput: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: '#667eea'
  },
  checkboxText: {
    color: '#333'
  },
  checkboxRequired: {
    fontSize: '12px',
    color: '#dc3545',
    marginTop: '4px'
  }
};

// Add animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  @keyframes pulseDot {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.8); }
  }
`;
document.head.appendChild(styleSheet);

export default QRScanner;